/**
 * Recursively merges two objects or arrays using the provided merge function and overwrite flag.
 */
function mergeAndCompare(
  mergeFn: (a: any, b: any, overwrite: boolean) => any,
  a: any,
  b: any,
  overwrite: boolean
): any {
  // Handle non-object inputs
  if (!isObject(a) && !Array.isArray(a)) return a;

  if (!isObject(b) && !Array.isArray(b)) return b;

  const result: any = Array.isArray(a) ? [] : {};

  // Only own, enumerable string and symbol keys
  const keys = new Set<string | symbol>();

  if (a) {
    Object.keys(a).forEach((key) => keys.add(key));

    Object.getOwnPropertySymbols(a).forEach((key) => keys.add(key));
  }

  if (b) {
    Object.keys(b).forEach((key) => keys.add(key));

    Object.getOwnPropertySymbols(b).forEach((key) => keys.add(key));
  }

  for (const key of Array.from(keys)) {
    if (a && b && key in a && key in b) {
      result[key] = mergeFn(a[key], b[key], overwrite);
    } else if (b && key in b) {
      if (!overwrite && (b[key] === null || b[key] === undefined)) {
        continue;
      }

      result[key] = b[key];
    } else if (a && key in a) {
      result[key] = a[key];
    }
  }

  return result;
}

function isObject(obj: any): boolean {
  return obj && typeof obj === "object" && !Array.isArray(obj);
}

/**
 * Merges objects, always overwriting arrays with later values.
 * If the last argument is a boolean, it is treated as the 'overwrite' flag for non-array objects.
 * @param objects - Objects to merge. The last argument can be a boolean to control overwrite behavior.
 * @returns The merged object
 */
export function mergeNonNull<T extends Record<string | symbol, any>>(...args: (T | boolean)[]): T {
  let overwrite = true;
  const objects: T[] = [];

  // Process args to separate objects from the boolean flag
  for (let i = 0; i < args.length; i++) {
    if (i === args.length - 1 && typeof args[i] === "boolean") {
      overwrite = args[i] as boolean;
    } else if (isObject(args[i]) || Array.isArray(args[i])) {
      objects.push(args[i] as T);
    }
  }

  if (objects.length === 0) return {} as T;

  if (objects.length === 1) return objects[0];

  const customMerge = (target: any, source: any, overwrite: boolean) => {
    if (Array.isArray(target) && Array.isArray(source)) {
      return source; // Always overwrite arrays
    }

    if (!overwrite && (source === null || source === undefined)) {
      return target;
    }

    if (isObject(target) && isObject(source)) {
      return mergeAndCompare(customMerge, target, source, overwrite);
    }

    return source;
  };

  // Always use custom merge logic
  return objects.reduce((result, obj) => mergeAndCompare(customMerge, result, obj, overwrite), {} as T);
}
